'use client';
import { PropsWithChildren, useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';

import VillagesList from './List';
import VillagesHeader from '../VillagesHeader';
import { VillagesHeaderDicts } from '../VillagesHeader/Filter';
import Map from '../Map';

import { cn } from '@/lib/utils';

import { VillageCardDictonary } from '../VillageCard/Root';
import { VillagesData } from '@/actions/villages';
import useFilterContext from '@/hooks/useFilterContext';
import useGlobalContext from '@/hooks/useGlobalContext';
import GlobalLoader from '../GlobalLoader';
import schema, { FormValues } from '../VillagesHeader/schema';
import { PaginationFooter } from '@/components/VillagesWrapper/PaginationFooter';

export type SearchParams = {
  currency?: string;
  catalogId?: string;
  region?: number;
  searchParams: { [key: string]: string | undefined };
  page: number;
};

type Props = {
  lang: string;
  villagesData: VillagesData;
  dicts: VillagesHeaderDicts & VillageCardDictonary;
  headerTitle: string;
  catalog: Catalog | undefined;
  headerPaths: {
    label: string;
    path?: string | undefined;
  }[];
  params: SearchParams;
  currency?: string;
} & PropsWithChildren;

export default function VillagesWrapper({
  lang,
  villagesData,
  headerTitle,
  catalog,
  headerPaths,
  dicts,
  children,
  params,
  currency,
}: Props) {
  const [allVillagesAreFetched, setAllVillagesAreFetched] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  // const [villages, setVillages] = useState<Village[]>(villagesData.data);
  const [showMap, setShowMap] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [heightReference, setHeightReference] = useState<number | undefined>(
    undefined,
  );

  const searchParams = useSearchParams();
  const adultParam = searchParams.get('adults');
  const childrenParam = searchParams.get('children');
  const infantsParam = searchParams.get('infants');
  const startDateParam = searchParams.get('startDate');
  const endDateParam = searchParams.get('endDate');
  const atPugliaPage = params.region && params.region === 2;

  const {
    villageFilter,
    isLoading: filterIsLoading,
    setIsLoading: setFilterIsLoading,
    quantities,
    setQuantities,
    selectedDates,
    setSelectedDates,
  } = useFilterContext();
  const { setIsDynamicUrl, setDynamicUrl } = useGlobalContext();

  const {
    villages: villagesLabel,
    singularSearchResult,
    pluralSearchResult,
  } = dicts.contentDict;
  const searchResult = params.searchParams.q;
  // const noResultsFound = villages.length === 0;

  const formQuantities = getFormQuantities();
  const formDates = getFormDates();

  function onSwitch(mapVisibility: boolean) {
    setShowMap(mapVisibility);
  }

  const filterForm = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      dates: formDates,
      quantities: formQuantities,
      destination: dicts.destinationsDict[0],
    },
  });

  function getFormDates() {
    if (startDateParam && endDateParam) {
      return {
        start: moment(startDateParam).utc().toDate(),
        end: moment(endDateParam).utc().toDate(),
      };
    }

    return {
      start: null,
      end: null,
    };
  }

  function getFormQuantities() {
    let adults = 0;
    let infants = 0;
    let children = 0;
    if (infantsParam) {
      infants = parseInt(infantsParam);
    }
    if (adultParam) {
      adults = parseInt(adultParam);
    }
    if (childrenParam) {
      children = parseInt(childrenParam);
    }

    return {
      adults,
      infants,
      children,
    };
  }

  // get new villages on the frontend and update the state
  // unused with the pagination, but left here in case its necessary later
  // const getAllVillages = useCallback(async () => {
  //   let canFetch = true;
  //   let offset = 6;
  //   let villages: Village[] = [...villagesData.data];

  //   console.log('getAllVillages');

  //   const functionParams = params;

  //   if (villageFilter) {
  //     offset = 0;
  //     villages = [];
  //     functionParams.searchParams = villageFilter;
  //   }

  //   setIsLoading(true);
  //   while (canFetch) {
  //     const response = await fetchVillages({
  //       offset,
  //       ...functionParams,
  //     });

  //     villages = [...villages, ...response.data];

  //     offset += 6;

  //     if (!response.pagination.next) {
  //       canFetch = false;
  //       setAllVillagesAreFetched(true);
  //       setVillages(villages);
  //       setIsLoading(false);
  //       setFilterIsLoading(false);
  //     }
  //   }
  // }, [
  //   villagesData.data,
  //   villageFilter,
  //   params,
  //   setIsLoading,
  //   setFilterIsLoading,
  // ]);

  useEffect(() => {
    const headerElement = document
      .querySelector('#villages-catalogs')
      ?.getBoundingClientRect();
    setHeightReference(headerElement?.top);

    const catalogSlugs =
      catalog &&
      catalog.translate.reduce(
        (acc: Record<string, string>, { locale, slug }) => {
          let path;
          switch (locale) {
            case 'it':
              path = `/it/sicilia/${slug}`;
              break;
            case 'de':
              path = `/de/sizilien/${slug}`;
              break;
            case 'fr':
              path = `/fr/sicile/${slug}`;
              break;
            default:
              path = `/sicily/${slug}`;
          }

          acc[locale] = path;
          return acc;
        },
        {},
      );

    catalog ? setIsDynamicUrl(true) : setIsDynamicUrl(false);
    setDynamicUrl(catalogSlugs);

    return () => {
      setIsDynamicUrl(false);
      setDynamicUrl({
        it: '',
        en: '',
        de: '',
        fr: '',
      });
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!heightReference) return;

    window.addEventListener('scroll', isSticky);
    return () => {
      window.removeEventListener('scroll', isSticky);
    };
    // eslint-disable-next-line
  }, [heightReference]);

  const isSticky = (e: Event) => {
    const mapRoot = document.querySelector('#map-root');
    const scrollTop = window.scrollY;
    if (heightReference && scrollTop >= heightReference) {
      mapRoot?.classList.add('is-sticky');
    } else {
      mapRoot?.classList.remove('is-sticky');
    }
  };

  useEffect(() => {
    if (villageFilter && showMap) {
      setIsLoading(true);
      if (allVillagesAreFetched) {
        setAllVillagesAreFetched(false);
      }
    }
  }, [allVillagesAreFetched, showMap, villageFilter]);

  // useEffect(() => {
  //   if (villageFilter && isLoading) {
  //     getAllVillages();
  //     return;
  //   }
  // }, [getAllVillages, isLoading, villageFilter]);

  // useEffect(() => {
  //   if (showMap && !allVillagesAreFetched) {
  //     getAllVillages();
  //   }
  // }, [allVillagesAreFetched, getAllVillages, showMap, villageFilter]);

  useEffect(() => {
    if (!villagesData.pagination.next) {
      setAllVillagesAreFetched(true);
    }
    const formQuantities = getFormQuantities();
    const hasQuantityFromUrl = Object.values(formQuantities).some(
      (quantity) => quantity > 0,
    );

    if (startDateParam && endDateParam) {
      setSelectedDates({
        start: moment(startDateParam).utc().toDate(),
        end: moment(endDateParam).utc().toDate(),
      });
    } else {
      if (selectedDates) {
        setSelectedDates(undefined);
      }
    }

    if (hasQuantityFromUrl) {
      setQuantities(formQuantities);
    }
    if (quantities && !hasQuantityFromUrl) {
      setQuantities(undefined);
    }
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <VillagesHeader.Root
        title={searchResult ? villagesLabel : headerTitle}
        paths={headerPaths}>
        <VillagesHeader.Mobile
          form={filterForm}
          modalIsOpen={modalIsOpen}
          setModalIsOpen={setModalIsOpen}
          lang={lang}
          dicts={dicts}
        />
        <VillagesHeader.Filter
          form={filterForm}
          lang={lang}
          className="hidden desktop:flex"
          dicts={dicts}>
          <VillagesHeader.Switch isLoading={isLoading} onSwitch={onSwitch} />
        </VillagesHeader.Filter>
      </VillagesHeader.Root>
      <div className="w-full flex flex-col px-sm md:px-md desktop:px-lg">
        <div
          id="header-ghost"
          className="hidden mb-[196px] [&.active]:desktop:block"
        />
        {children}
        {searchResult && (
          <h3 className="text-medium mb-2 font-title">
            {villagesData.count}{' '}
            {villagesData.count > 1 ? pluralSearchResult : singularSearchResult}{' '}
            {searchResult}
          </h3>
        )}
        <div
          className={cn({
            'flex flex-wrap': showMap,
          })}>
          <VillagesList
            dicts={{
              regionZoneDict: dicts.regionZoneDict,
              currencyDict: dicts.currencyDict,
              villageDict: dicts.villageDict,
            }}
            isLoadingWithMap={isLoading}
            villages={villagesData.data}
            showMap={showMap}
            lang={lang}
            currency={currency}
          />

          {showMap &&
            (isLoading ? (
              <Map.Loader />
            ) : (
              <div
                id="map-root"
                className={cn(
                  'hidden desktop:block h-full ml-[1.875rem] pb-[1.875rem] z-10 relative top-0 [&.is-sticky]:desktop:sticky [&.is-sticky]:desktop:top-[137px]',
                  {
                    'w-[66.66667%] flex-[0_0_auto]': showMap,
                  },
                )}>
                <Map.Root
                  customCenter={
                    atPugliaPage
                      ? {
                          lat: 41.0608651,
                          lng: 15.9659781,
                        }
                      : undefined
                  }
                  lang={lang}
                  villages={villagesData.data}
                />
                <GlobalLoader
                  className="absolute"
                  isLoading={filterIsLoading}
                />
              </div>
            ))}
        </div>

        <PaginationFooter
          villagesData={villagesData}
          currentPage={params.page}
        />
      </div>
    </>
  );
}
